package com.sddamico.swigmore.pages

import androidx.compose.runtime.Composable
import com.sddamico.swigmore.components.layouts.ContentColumn
import com.sddamico.swigmore.components.layouts.SignedInPage
import com.sddamico.swigmore.ds.Reveal
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.core.PageTitle
import com.sddamico.swigmore.ds.core.SortableTable
import com.sddamico.swigmore.ds.core.TableColumn
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.feedback.PageTitleSkeleton
import com.sddamico.swigmore.ds.feedback.Skeleton
import com.sddamico.swigmore.ds.feedback.SkeletonBlock
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeDisplay
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.ds.typeTeam
import com.sddamico.swigmore.models.ComputerPollResponse
import com.sddamico.swigmore.models.ComputerPollRowDto
import com.sddamico.swigmore.models.PollStanding
import com.sddamico.swigmore.models.RankingRules
import com.sddamico.swigmore.models.RatedRankDto
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.CachedResource
import com.sddamico.swigmore.util.LoadState
import com.sddamico.swigmore.util.NOT_FINAL
import com.sddamico.swigmore.util.fixed
import com.sddamico.swigmore.util.rememberCachedResource
import com.sddamico.swigmore.util.share
import com.sddamico.swigmore.util.signedFixed
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Colley, Elo, Massey, all-play and Pythagorean, blended BCS-style with the coaches poll; one sortable row per team. */
@Page
@Composable
fun ComputerPollsPage() {
  SignedInPage("Computer Polls", activeTab = AppTab.ComputerPolls) {
    ContentColumn {
      when (val poll = rememberCachedResource(CachedResource.ComputerPoll).state) {
        LoadState.Loading -> TableSkeleton()
        is LoadState.Failed -> Reveal { PageTitle("Computer polls", "The computers are down.", lede = poll.message) }
        is LoadState.Ready -> Reveal { ComputerPolls(poll.value) }
      }
    }
  }
}

@Composable
private fun ComputerPolls(poll: ComputerPollResponse) {
  val through = poll.throughWeek
  PageTitle(
    eyebrow = listOfNotNull(
      through?.let { if (poll.throughWeekFinal) "Through week $it" else "Through week $it ($NOT_FINAL)" },
      poll.coachesPollWeek?.let { "Week $it coaches poll" },
    ).joinToString(" · ").ifEmpty { "Computer polls" },
    title = "The Computer Polls",
    lede = when {
      through == null -> "The computers rate every team once the first week's Fantrax scores are in."
      poll.coachesPollWeek == null ->
        "Colley, Elo and Massey rate every team on its games. The BCS ranking joins them once this week's " +
          "coaches poll is published. Tap a column to sort by it."
      else ->
        "Colley, Elo and Massey rate every team on its games, and BCS blends them with the coaches poll. " +
          "Tap a column to sort by it."
    },
  )
  if (poll.rows.isEmpty()) return

  val withPoll = poll.coachesPollWeek != null
  SortableTable(
    columns(withPoll),
    poll.rows,
    Modifier.css("margin-top" to "24px"),
    // Without the poll there is no BCS rank, so the table opens on Colley.
    initialSort = if (withPoll) 0 else 1,
  )

  Div(
    attrs = Modifier.typeBody("13px", lineHeight = "1.6")
      .css("color" to SwColor.textMuted, "margin-top" to "16px", "max-width" to "760px").toAttrs()
  ) {
    Text(
      "Once the coaches poll is in, BCS averages a share between 0 and 1 from each source: the coaches poll's points over the most a team could " +
        "get, and (n + 1 − rank) / n from Colley, Elo and Massey. Colley counts only wins and losses against the " +
        "schedule. Elo starts everyone at ${RankingRules.ELO_START} and moves ${RankingRules.ELO_K / 2} points a game " +
        "at even odds. Massey rates in points: the gap between two teams is the margin expected between them. " +
        "All-play is the record against every team every week, Pythag the win share expected from points for and " +
        "against (exponent ${RankingRules.PYTHAGOREAN_EXPONENT}); neither counts toward BCS. SoS is the opponents' " +
        "average Colley rating.",
    )
  }
}

/** [withPoll] adds the coaches poll and the BCS rank built on it. */
private fun columns(withPoll: Boolean): List<TableColumn<ComputerPollRowDto>> = listOfNotNull(
  if (withPoll) TableColumn("#", byRank { it.composite!! }, widthPx = 44, alignEnd = false, sticky = true) {
    Span(attrs = Modifier.typeDisplay("20px").toAttrs()) { Text(it.composite!!.rank.toString()) }
  } else null,
  TableColumn("Team", compareBy { it.team.name.lowercase() }, widthPx = 170, alignEnd = false, sticky = true) {
    Div(attrs = Modifier.typeTeam("13px").css("white-space" to "normal").toAttrs()) { Text(it.team.name) }
    it.owner?.let { owner ->
      Div(attrs = Modifier.typeBody("12px", 500, "normal").css("color" to SwColor.textMuted, "margin-top" to "2px").toAttrs()) {
        Text(owner)
      }
    }
  },
  if (withPoll) TableColumn("BCS", byRank { it.composite!! }) {
    Readout(share(it.composite!!.rating, 4), strong = true)
  } else null,
  if (withPoll) TableColumn("Coaches", compareByDescending { it.coaches?.points ?: 0 }, alignEnd = false) {
    CoachesCell(it)
  } else null,
  rankedColumn("Colley", "5ch", { it.colley }) { share(it.colley.rating, 4) },
  rankedColumn("Elo", "4ch", { it.elo }) { fixed(it.elo.rating, 0) },
  rankedColumn("Massey", "5ch", { it.massey }) { signedFixed(it.massey.rating, 1) },
  rankedColumn("All-play", "7ch", { it.allPlay }) { it.allPlayRecord.toString() },
  rankedColumn("Pythag", "4ch", { it.pythagorean }) { share(it.pythagorean.rating, 3) },
  TableColumn("Record", compareByDescending<ComputerPollRowDto> { it.record.winShare }.thenByDescending { it.record.wins }) {
    Readout(it.record.toString())
  },
  TableColumn("PPG", compareByDescending { it.pointsPerGame }) { Readout(fixed(it.pointsPerGame, 1)) },
  TableColumn("PF", compareByDescending { it.pointsFor }) { Readout(fixed(it.pointsFor, 1)) },
  TableColumn("PA", compareBy { it.pointsAgainst }) { Readout(fixed(it.pointsAgainst, 1)) },
  rankedColumn("SoS", "5ch", { it.strengthOfSchedule }) { share(it.strengthOfSchedule.rating, 4) },
)

/** A method's rank and rating, its rating right-aligned in a box sized to the column's format so the digits line up. */
private fun rankedColumn(
  label: String,
  ratingWidth: String,
  rated: (ComputerPollRowDto) -> RatedRankDto,
  rating: (ComputerPollRowDto) -> String,
) = TableColumn(label, byRank(rated), alignEnd = false) { row ->
  RankLabel(rated(row).rank.toString(), "16px", SwColor.cream)
  Span(
    attrs = Modifier.typeMono("11px")
      .css("color" to SwColor.textMuted, "display" to "inline-block", "min-width" to ratingWidth, "text-align" to "right").toAttrs()
  ) {
    Text(rating(row))
  }
}

private fun byRank(rated: (ComputerPollRowDto) -> RatedRankDto): Comparator<ComputerPollRowDto> =
  compareBy<ComputerPollRowDto> { rated(it).rank }.thenBy { it.team.name.lowercase() }

@Composable
private fun Readout(text: String, strong: Boolean = false) {
  Span(
    attrs = Modifier.typeMono("12px", if (strong) 700 else 400)
      .css("color" to if (strong) SwColor.gold else SwColor.textBody).toAttrs()
  ) { Text(text) }
}

@Composable
private fun CoachesCell(row: ComputerPollRowDto) {
  val coaches = row.coaches ?: return
  val (label, color) = when (val standing = coaches.standing) {
    is PollStanding.Ranked -> standing.rank.toString() to SwColor.cream
    PollStanding.ReceivingVotes -> "RV" to SwColor.gold
    PollStanding.Unranked -> "—" to SwColor.textFaint
  }
  RankLabel(label, if (coaches.standing is PollStanding.Ranked) "16px" else "12px", color)
  Span(
    attrs = Modifier.typeMono("11px")
      .css("color" to SwColor.textMuted, "display" to "inline-block", "min-width" to "7ch").toAttrs()
  ) {
    Text(if (coaches.points > 0) "${coaches.points} pts" else "")
  }
}

/** One- and two-digit ranks, "RV" and "—" differ in width, so a fixed box keeps them, and what follows, in line. */
@Composable
private fun RankLabel(text: String, size: String, color: String) {
  Span(
    attrs = Modifier.typeDisplay(size)
      .css("color" to color, "display" to "inline-block", "width" to "32px").toAttrs()
  ) {
    Text(text)
  }
}

@Composable
private fun TableSkeleton() {
  Skeleton {
    PageTitleSkeleton()
    SkeletonBlock("640px", Modifier.css("margin-top" to "24px"))
  }
}
